import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { UnviewedActionPlan, ApiResponse } from '@/types'

// Fetch unviewed action plan count (polls every 60s for banner badge)
export function useUnviewedCount(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true
  return useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: async (): Promise<number> => {
      const res = await fetch('/api/notifications/unviewed-count')
      const data: ApiResponse<{ count: number }> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch count')
      return data.data!.count
    },
    enabled,
    refetchInterval: enabled ? 120000 : false,
  })
}

// Fetch list of unviewed action plans with goal/metric context
export function useUnviewedActionPlans() {
  return useQuery({
    queryKey: ['notifications', 'unviewed'],
    queryFn: async (): Promise<UnviewedActionPlan[]> => {
      const res = await fetch('/api/notifications/unviewed-action-plans')
      const data: ApiResponse<UnviewedActionPlan[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch unviewed plans')
      return data.data!
    },
  })
}

// Mark action plans as viewed (invalidates both count and list queries)
export function useMarkViewed() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (planIds: string[]) => {
      const res = await fetch('/api/notifications/mark-viewed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_plan_ids: planIds }),
      })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to mark as viewed')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
